import {
  collection, doc, setDoc, updateDoc, deleteDoc, deleteField, getDocs, getDoc,
  onSnapshot, query, orderBy, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase.js';
import { createIdempotencyKey, runClientIdempotent } from './idempotency.js';

const coursesCol = (uid) => collection(db, 'users', uid, 'courses');
const courseDoc = (uid, courseId) => doc(db, 'users', uid, 'courses', courseId);

export async function createCourse(uid, {
  title, description = '', emoji = '📘', color = '#cc785c', idempotencyKey = createIdempotencyKey('createCourse'),
}) {
  return runClientIdempotent({
    uid,
    operation: 'createCourse',
    key: idempotencyKey,
    payload: { title, description, emoji, color },
    handler: async () => {
      const ref = doc(coursesCol(uid));
      await setDoc(ref, {
        title, description, emoji, color,
        order: Date.now(),
        // Provenance/licensing (see functions/marketplace.js's resale
        // guard): a course created directly by its owner is 'authored' and
        // resalable by default. Nothing sets these to 'purchased'/
        // 'subscribed' + resalable:false anymore (that was the now-removed
        // "extend it further" remix copy, Admin SDK only) — some legacy
        // courses from before its removal still carry it. firestore.rules
        // locks `resalable` from ever being changed by a direct client
        // write either way, so this can never be flipped back client-side.
        sourceType: 'authored',
        resalable: true,
        sourceListingId: null,
        schoolCode: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        idempotencyKey,
      });
      return ref.id;
    },
  });
}

export async function updateCourse(uid, courseId, patch) {
  await updateDoc(courseDoc(uid, courseId), { ...patch, updatedAt: serverTimestamp() });
}

export function watchCourses(uid, callback) {
  const q = query(coursesCol(uid), orderBy('order', 'asc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export async function getCourse(uid, courseId) {
  const snap = await getDoc(courseDoc(uid, courseId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// One-time fetch (no live subscription) — used by the "file a purchase
// under a course" picker, which is opened inside a short-lived modal.
// Trashed (soft-deleted) courses are excluded — they shouldn't show up as
// a destination while they're sitting in the Recycle Bin.
export async function getCourses(uid) {
  const snap = await getDocs(query(coursesCol(uid), orderBy('order', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => !c.deletedAt);
}

// ── Recycle Bin (soft delete) ───────────────────────────────────────────
// "Delete course" from the UI moves it to the Recycle Bin by stamping
// deletedAt instead of destroying anything. The course (and everything
// under it) is left completely untouched otherwise, so restoring is just
// clearing that one field. A scheduled Cloud Function (see
// functions/purgeTrash.js) permanently deletes anything still trashed
// after TRASH_RETENTION_DAYS (see lib/trash.js) — the same
// permanentlyDeleteCourse path below, run server-side with elevated
// privileges so it works even if the user never reopens the app.
export async function trashCourse(uid, courseId) {
  await updateDoc(courseDoc(uid, courseId), { deletedAt: serverTimestamp() });
}

export async function restoreCourse(uid, courseId) {
  await updateDoc(courseDoc(uid, courseId), { deletedAt: deleteField() });
}

// Deletes a course: all topics, all their messages, and every file under
// userFiles/{uid}/{courseId}/ in Storage. Client-initiated, rules-permitted
// (the user owns every path being touched) — see "Delete means delete" in
// README. This is the PERMANENT, unrecoverable path — used for "Delete
// forever" out of the Recycle Bin, the 7-day auto-purge, and full account
// deletion. The normal "Delete course" button in the UI calls
// trashCourse() above instead.
export async function deleteCourse(uid, courseId) {
  const topicsSnap = await getDocs(collection(db, 'users', uid, 'courses', courseId, 'topics'));
  for (const topicDoc of topicsSnap.docs) {
    await deleteTopicDocsOnly(uid, courseId, topicDoc.id);
  }
  await deleteStorageFolder(`userFiles/${uid}/${courseId}`);
  await deleteDoc(courseDoc(uid, courseId));
}

async function deleteTopicDocsOnly(uid, courseId, topicId) {
  const messagesSnap = await getDocs(collection(db, 'users', uid, 'courses', courseId, 'topics', topicId, 'messages'));
  const batch = writeBatch(db);
  messagesSnap.docs.forEach((m) => batch.delete(m.ref));
  batch.delete(doc(db, 'users', uid, 'courses', courseId, 'topics', topicId));
  await batch.commit();
}

export async function deleteStorageFolder(prefix) {
  const folderRef = ref(storage, prefix);
  let list;
  try {
    list = await listAll(folderRef);
  } catch (e) {
    return; // nothing there
  }
  await Promise.all(list.items.map((item) => deleteObject(item).catch(() => {})));
  await Promise.all(list.prefixes.map((p) => deleteStorageFolder(p.fullPath)));
}
